import InvoiceRow from '@/components/invoices/InvoiceRow'
import PaymentModal from '@/components/invoices/PaymentModal'
import { invoiceFilterLabels } from '@/constants'
import { useInvoiceActions } from '@/hooks/useInvoiceActions'
import { EInvoicingConnection, Invoice, InvoiceFilter, InvoiceWithContract, PaginationMeta, SharedProps } from '@/types'
import { formatCurrency } from '@/utils/format'
import { Head, Link, router, usePage } from '@inertiajs/react'
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Chip,
  Group,
  Pagination,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core'
import { useDebouncedCallback } from '@mantine/hooks'
import { IconAlertTriangle, IconFileInvoice, IconRefresh, IconSearch, IconX } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { ChangeEvent, useState } from 'react'

type InvoiceFilters = { status: InvoiceFilter; period: string | null; search: string | null }

type InvoiceTotals = {
  to_handle: number
  overdue_amount: string | number
  overdue_count: number
  awaiting_amount: string | number
  collected_this_month: string | number
}

const PERIOD_OPTIONS_COUNT = 18

// The last months to pick a billed period from, plus the one already filtered on if it is older.
const periodOptions = (selected: string | null) => {
  const months = Array.from({ length: PERIOD_OPTIONS_COUNT }, (_, index) => dayjs().subtract(index, 'month'))
  const values = months.map((month) => month.format('YYYY-MM'))
  if (selected && !values.includes(selected)) values.unshift(selected)
  return values.map((value) => ({ value, label: dayjs(`${value}-01`).format('MMMM YYYY') }))
}

function CounterCard({
  label,
  value,
  detail,
  color,
  filter,
  onSelect,
}: {
  label: string
  value: string
  detail?: string
  color?: string
  filter: InvoiceFilter
  onSelect: (filter: InvoiceFilter) => void
}) {
  const handleClick = () => onSelect(filter)

  return (
    <UnstyledButton onClick={handleClick} h="100%">
      <Paper withBorder radius="md" p="md" h="100%">
        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
          {label}
        </Text>
        <Text size="xl" fw={700} c={color}>
          {value}
        </Text>
        <Text size="xs" c="dimmed">
          {detail ?? '\u00a0'}
        </Text>
      </Paper>
    </UnstyledButton>
  )
}

export default function InvoicesIndex({
  invoices,
  filters,
  counts,
  totals,
  pagination,
  connection,
}: {
  invoices: InvoiceWithContract[]
  filters: InvoiceFilters
  counts: Record<InvoiceFilter, number>
  totals: InvoiceTotals
  pagination: PaginationMeta
  connection: EInvoicingConnection | null
}) {
  const { current_organization } = usePage<SharedProps>().props
  const basePath = `/organizations/${current_organization?.slug}`
  const [searchInput, setSearchInput] = useState(filters.search ?? '')
  const { invoicePath, resend, cancelAndRedo, openPdf, payingInvoice, startPayment, closePayment } =
    useInvoiceActions()

  const updateFilters = (updates: Partial<InvoiceFilters> & { page?: number }) => {
    const next = { ...filters, page: undefined, ...updates }
    const cleaned = Object.fromEntries(Object.entries(next).filter(([, value]) => value !== null && value !== undefined && value !== ''))
    router.get(`${basePath}/invoices`, cleaned, { preserveState: true, preserveScroll: true })
  }

  const submitSearch = useDebouncedCallback((value: string) => updateFilters({ search: value.trim() || null }), 300)

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.currentTarget.value)
    submitSearch(event.currentTarget.value)
  }

  const handleClearSearch = () => {
    setSearchInput('')
    updateFilters({ search: null })
  }

  const handleStatusChange = (value: string) => updateFilters({ status: value as InvoiceFilter })

  const handleSelectCounter = (status: InvoiceFilter) => updateFilters({ status })

  const handlePeriodChange = (value: string | null) => updateFilters({ period: value })

  const handlePageChange = (page: number) => updateFilters({ page })

  const handleSelect = (invoice: Invoice) => router.get(invoicePath(invoice))

  const handleSynchronize = () => router.post(`${basePath}/e_invoicing_synchronization`)

  const isConnected = !!connection?.connected_at

  return (
    <>
      <Head title={`Factures - ${current_organization?.name}`} />

      <Stack gap="md">
        <Group justify="space-between">
          <Title order={3}>Factures</Title>
          <Group gap="sm">
            {isConnected && (
              <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={handleSynchronize}>
                Actualiser
              </Button>
            )}
            <Button component={Link} href={`${basePath}/invoice_batches/new`} leftSection={<IconFileInvoice size={16} />}>
              Facturer le mois
            </Button>
          </Group>
        </Group>

        {!isConnected && (
          <Alert variant="light" color="yellow" icon={<IconAlertTriangle />}>
            La facture électronique n'est pas encore activée.{' '}
            <Anchor component={Link} href={`${basePath}/e_invoicing_setting`}>
              Connecter la plateforme agréée
            </Anchor>
          </Alert>
        )}

        {connection?.last_error && (
          <Alert variant="light" color="red" icon={<IconAlertTriangle />}>
            Plateforme : {connection.last_error}
          </Alert>
        )}

        <SimpleGrid cols={{ base: 2, md: 4 }}>
          <CounterCard
            label="À traiter"
            value={String(totals.to_handle)}
            detail="échecs, refus et litiges"
            color={totals.to_handle > 0 ? 'red' : undefined}
            filter="to_handle"
            onSelect={handleSelectCounter}
          />
          <CounterCard
            label="En retard"
            value={formatCurrency(totals.overdue_amount)}
            detail={`${totals.overdue_count} facture${totals.overdue_count > 1 ? 's' : ''}`}
            color={totals.overdue_count > 0 ? 'orange' : undefined}
            filter="overdue"
            onSelect={handleSelectCounter}
          />
          <CounterCard
            label="À encaisser"
            value={formatCurrency(totals.awaiting_amount)}
            filter="awaiting_payment"
            onSelect={handleSelectCounter}
          />
          <CounterCard
            label="Encaissé ce mois"
            value={formatCurrency(totals.collected_this_month)}
            color="green"
            filter="settled"
            onSelect={handleSelectCounter}
          />
        </SimpleGrid>

        <Group justify="space-between" align="flex-end">
          <Chip.Group multiple={false} value={filters.status} onChange={handleStatusChange}>
            <Group gap={6}>
              {(Object.keys(invoiceFilterLabels) as InvoiceFilter[]).map((filter) => (
                <Chip key={filter} value={filter} size="sm">
                  {`${invoiceFilterLabels[filter]} (${counts[filter]})`}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
          <Group gap="sm">
            <Select
              aria-label="Période facturée"
              placeholder="Toutes les périodes"
              data={periodOptions(filters.period)}
              value={filters.period}
              onChange={handlePeriodChange}
              clearable
              clearButtonProps={{ 'aria-label': 'Toutes les périodes' }}
              w={190}
            />
            <TextInput
              placeholder="Client ou n° de facture"
              leftSection={<IconSearch size={16} />}
              rightSection={
                searchInput ? (
                  <ActionIcon variant="subtle" color="gray" onClick={handleClearSearch} aria-label="Effacer la recherche">
                    <IconX size={16} />
                  </ActionIcon>
                ) : null
              }
              value={searchInput}
              onChange={handleSearchChange}
              w={260}
            />
          </Group>
        </Group>

        <Paper withBorder radius="md" p={{ base: 0, sm: 'md' }}>
          <Table.ScrollContainer minWidth={1050}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>N°</Table.Th>
                  <Table.Th>Client</Table.Th>
                  <Table.Th>Période</Table.Th>
                  <Table.Th>Émise le</Table.Th>
                  <Table.Th>Échéance</Table.Th>
                  <Table.Th ta="right">Montant TTC</Table.Th>
                  <Table.Th ta="right">Reste dû</Table.Th>
                  <Table.Th>Statut</Table.Th>
                  <Table.Th w={120} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {invoices.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={9}>
                      <Text c="dimmed" ta="center" py="md">
                        Aucune facture
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {invoices.map((invoice) => (
                  <InvoiceRow
                    key={invoice.id}
                    invoice={invoice}
                    onSelect={handleSelect}
                    onPay={startPayment}
                    onResend={resend}
                    onCancelAndRedo={cancelAndRedo}
                    onOpenPdf={openPdf}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>

        {pagination.total_pages > 1 && (
          <Group justify="center">
            <Pagination total={pagination.total_pages} value={pagination.current_page} onChange={handlePageChange} />
          </Group>
        )}
      </Stack>

      <PaymentModal invoice={payingInvoice} onClose={closePayment} />
    </>
  )
}
